import moment from 'moment';
import { Cacheavel, CachePersistente, Key, Store } from './cache.interface';

abstract class CachePersistenteBase<T extends Cacheavel> {
  protected garanteQueCacheadoNaoEstaExpirado(
    cached: T | undefined,
  ): T | undefined {
    if (cached && moment(cached.exp).isSameOrAfter(moment().utc())) {
      return cached;
    }
  }

  /**
   *  O método comCache abstrai acesso e definição de valores em cache
   *
   *  @example
   *   const cache = await CachePersistenteBaseImpl.init<AnyCacheavel>('store.key');
   *   const res = await cache.comCache(
   *     '/alpha-hastati/api/v4/empregados',
   *     async () => {
   *       const res: Empregados[] = await fetch(url, {
   *         method: 'GET',
   *         headers: { Authorization: 'Bearer ...' },
   *       }).then((r) => r.json());
   *
   *       return {
   *         exp: moment().add(1, 'hour').utc().toDate(),
   *         empregados,
   *       };
   *     },
   *   );
   */
  public async comCache(
    key: Key,
    func: () => Promise<T | undefined>,
  ): Promise<T | undefined> {
    const cacheado = await this.pegar(key);

    if (cacheado) return cacheado;

    const cacheavel = await func();

    if (cacheavel !== undefined) await this.cachear(key, cacheavel);

    return cacheavel;
  }

  public abstract pegar(key: Key): Promise<T | undefined>;

  public abstract cachear(key: Key, value: T): Promise<void>;
}

export class IndexedCachePersistente<
  T extends Cacheavel,
> extends CachePersistenteBase<T> {
  // Stores precisam ser cadastradas onupgradeneeded
  private readonly STORES = ['tiosam.faturas'] as const;
  // Sempre que adicionar uma store incremente a versão
  private readonly DBVERSION = 1 as const;

  private readonly dbName: string = 'gestao' as const;

  private db!: IDBDatabase;
  private store!: Store;

  public static async init<T extends Cacheavel>(
    store: Store,
  ): Promise<CachePersistente<T>> {
    const cache = new IndexedCachePersistente<T>();

    cache.store = store;

    cache.db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(cache.dbName, cache.DBVERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        for (const s of cache.STORES) {
          if (!db.objectStoreNames.contains(s)) {
            db.createObjectStore(s);
          }
        }
      };

      request.onsuccess = () => resolve(request.result);

      request.onerror = () => reject(request.error);
    });

    return cache;
  }

  public async pegar(key: Key): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const request = this.db
        .transaction(this.store, 'readonly')
        .objectStore(this.store)
        .get(key);

      request.onsuccess = () => {
        const cached = request.result as T | undefined;
        return resolve(this.garanteQueCacheadoNaoEstaExpirado(cached));
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async cachear(key: Key, value: T): Promise<void> {
    new Promise((resolve, reject) => {
      const request = this.db
        .transaction(this.store, 'readwrite')
        .objectStore(this.store)
        .put(value, key);

      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error);
    });
  }
}

export class CachePersistenteSimplificado<
  T extends Cacheavel,
> extends CachePersistenteBase<T> {
  private _cache!: Cache;

  public static async init<T extends Cacheavel>(
    store: Store,
  ): Promise<CachePersistente<T>> {
    const cache = new CachePersistenteSimplificado<T>();

    cache._cache = await caches.open(store);

    return cache;
  }

  public async pegar(key: Key): Promise<T | undefined> {
    const cached = await this._cache.match(key);

    if (cached)
      return this.garanteQueCacheadoNaoEstaExpirado(await cached.json());
  }

  public async cachear(key: Key, value: T): Promise<void> {
    await this._cache.put(key, new Response(JSON.stringify(value)));
  }
}
